import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PublicReportHeader from "../reports/PublicReportHeader";
import { reviewScope, useApprovalReview } from "../../hooks/useApprovals";
import ApprovalReviewPanel from "./ApprovalReviewPanel";
import { NameDialog, readName, writeName } from "./ReviewerName";
import { CreatorCell, StatusChip, cardSx, errorMessage } from "./approvalUi";

// /script-review/:token, /video-review/:token — the no-login page behind a
// share link. A brand link lists every creator on the board; a creator link
// opens just their own item. Which one it is comes from the server (the token
// decides), never the URL shape.

const ApprovalReviewPage = ({ kind }) => {
  const { token } = useParams();
  const { data, isLoading, error } = useApprovalReview(kind, token);
  const [reviewerName, setReviewerName] = useState(readName);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const items = useMemo(() => data?.[kind.plural] ?? [], [data, kind]);
  const isBrand = data?.party === "brand";

  useEffect(() => {
    if (!selectedId && items.length) setSelectedId(items[0].id);
  }, [items, selectedId]);

  // Only the brand is asked — a creator link already knows who it's for and
  // falls back to the creator's handle server-side.
  useEffect(() => {
    if (isBrand && !reviewerName) setNameDialogOpen(true);
  }, [isBrand, reviewerName]);

  const scope = useMemo(() => reviewScope(kind, token, reviewerName), [kind, token, reviewerName]);

  const saveName = (name) => {
    writeName(name);
    setReviewerName(name);
    setNameDialogOpen(false);
  };

  const shell = (children) => (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <PublicReportHeader
        campaignName={data?.plan?.name}
        badgeLabel={data ? (isBrand ? "Brand review" : "Creator review") : `${kind.Noun} review`}
      />
      <Box sx={{ pt: 10, pb: 4, px: { xs: 2, sm: 3 }, maxWidth: 1200, mx: "auto" }}>{children}</Box>
    </Box>
  );

  if (isLoading) {
    return shell(
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#FF5200" }} />
      </Box>
    );
  }
  if (error) {
    return shell(
      <Box sx={{ ...cardSx, p: 4, textAlign: "center", maxWidth: 520, mx: "auto" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>This link isn't available</Typography>
        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>{errorMessage(error, "Please ask the team for a new link.")}</Typography>
      </Box>
    );
  }

  const header = (
    <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {isBrand ? kind.copy.brandHeading : kind.copy.creatorHeading}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6B7280" }}>
          {data.plan.brandName ? `${data.plan.brandName} · ` : ""}
          {isBrand ? kind.copy.brandSubtitle : kind.copy.creatorSubtitle}
        </Typography>
      </Box>
      {isBrand && reviewerName && (
        <Button size="small" onClick={() => setNameDialogOpen(true)} sx={{ textTransform: "none", color: "#6B7280" }}>
          Reviewing as {reviewerName} · change
        </Button>
      )}
    </Box>
  );

  if (!items.length) {
    return shell(
      <>
        {header}
        <Box sx={{ ...cardSx, p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#6B7280" }}>There are no {kind.plural} to review yet.</Typography>
        </Box>
      </>
    );
  }

  return shell(
    <>
      {header}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: isBrand ? "300px 1fr" : "1fr" }, gap: 2.5, alignItems: "start" }}>
        {isBrand && (
          <Box sx={{ ...cardSx, p: 1, maxHeight: { md: "calc(100vh - 200px)" }, overflowY: "auto" }}>
            {items.map((s) => (
              <Box
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                sx={{
                  p: 1.25,
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  bgcolor: s.id === selectedId ? "rgba(255,82,0,0.08)" : "transparent",
                  "&:hover": { bgcolor: s.id === selectedId ? "rgba(255,82,0,0.08)" : "#F9FAFB" },
                }}
              >
                <CreatorCell item={s} />
                <Box sx={{ pl: "46px" }}><StatusChip status={s.status} /></Box>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 3 } }}>
          {selectedId && <ApprovalReviewPanel scope={scope} itemId={selectedId} viewer={data.party} />}
        </Box>
      </Box>
      <NameDialog key={String(nameDialogOpen)} open={nameDialogOpen} initial={reviewerName} onSave={saveName} />
    </>
  );
};

export default ApprovalReviewPage;
